import { memo } from 'react';
import { isViewReference } from './utils';
import { isEqualWith } from 'lodash';
import {
  hasComponentId,
  haveSameCallbacks,
  nodeEqualWith,
} from './viewComponentEquivalence';
import {
  DependencyMapNodeViewModel,
  DependencyMapReferenceViewModel,
  DependencyMapViewNodeChildrenProperties,
} from './types';
import { ReferenceDirection } from '@ardoq/api-types';

const hasComponentIdInChildren = (
  children:
    | (DependencyMapNodeViewModel | DependencyMapReferenceViewModel)[]
    | null,
  componentId: string | null
) => {
  return children && hasComponentId(children, componentId);
};

const checkComponentChange = (
  oldComponentId: string | null,
  newComponentId: string | null,
  children:
    | (DependencyMapNodeViewModel | DependencyMapReferenceViewModel)[]
    | null
) => {
  if (oldComponentId !== newComponentId) {
    const hasPreviousComponent = hasComponentIdInChildren(
      children,
      oldComponentId
    );
    if (hasPreviousComponent) {
      return true;
    }
    const hasNewComponent = hasComponentIdInChildren(children, newComponentId);
    if (hasNewComponent) {
      return true;
    }
  }
  return false;
};

const areViewNodeChildrenEffectivelyEqual = (
  oldProps: DependencyMapViewNodeChildrenProperties,
  newProps: DependencyMapViewNodeChildrenProperties
): boolean => {
  if (
    oldProps.colorByReference !== newProps.colorByReference ||
    !haveSameCallbacks(oldProps, newProps)
  ) {
    return false;
  }

  if (
    oldProps.node.children !== newProps.node.children &&
    !isEqualWith(oldProps.node.children, newProps.node.children, nodeEqualWith)
  ) {
    return false;
  }

  const { node } = newProps;

  if (
    checkComponentChange(
      oldProps.hoveredComponentId,
      newProps.hoveredComponentId,
      node.children
    ) ||
    checkComponentChange(
      oldProps.focusedComponentId,
      newProps.focusedComponentId,
      node.children
    ) ||
    checkComponentChange(
      oldProps.selectedComponentId,
      newProps.selectedComponentId,
      node.children
    )
  ) {
    return false;
  }

  return true;
};

const DependencyMapViewNodeChildren = memo(
  ({
    viewInstanceId,
    node: { children },
    colorByReference,
    selectedComponentId,
    hoveredComponentId,
    focusedComponentId,
    setHoveredComponentId,
    setFocusedComponentId,
    showVertical,
    referenceParents,
    onComponentClick,
    onComponentDoubleClick,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    contextMenuDataAttribute,
    onReferenceClick,
    isNewConditionalFormatting,
    DependencyMap,
  }: DependencyMapViewNodeChildrenProperties) => {
    const vertical = showVertical && children?.length === 1;
    return (
      <>
        {children?.map(child => {
          const {
            node,
            className,
            headerLabel,
            headerSubLabels,
            key,
            iconColor,
            fill,
            stroke,
            textColor,
            textModerateColor,
            referenceIds,
          } = isViewReference(child)
            ? {
                node: child.node,
                className: colorByReference
                  ? child.className
                  : child.node.className,
                iconColor: child.iconColor,
                fill: colorByReference ? child.fill : child.node.fill,
                stroke: colorByReference ? child.stroke : child.node.stroke,
                textColor: colorByReference
                  ? child.textColor
                  : child.node.textColor,
                textModerateColor: colorByReference
                  ? child.textModerateColor
                  : child.node.textModerateColor,
                headerLabel: child.label,
                headerSubLabels: child.otherLabels,
                key: child.referenceIds.join(','),
                referenceIds: child.referenceIds,
              }
            : {
                node: child,
                className: child.className,
                iconColor: child.iconColor,
                fill: child.fill,
                stroke: child.stroke,
                textColor: child.textColor,
                textModerateColor: child.textModerateColor,
                headerLabel: null,
                headerSubLabels: undefined,
                key: child.id,
                referenceIds: null,
              };
          return (
            <DependencyMap.ViewNode
              key={`child-${key}`}
              viewInstanceId={viewInstanceId}
              node={node}
              referenceDirection={
                referenceParents
                  ? ReferenceDirection.UNSET
                  : ReferenceDirection.NONE
              }
              headerLabel={headerLabel}
              headerSubLabels={headerSubLabels}
              subLabel={node.subLabel}
              colorByReference={colorByReference}
              className={className}
              iconColor={iconColor}
              fill={fill}
              stroke={stroke}
              textColor={textColor}
              textModerateColor={textModerateColor}
              selectedComponentId={selectedComponentId}
              hoveredComponentId={hoveredComponentId}
              focusedComponentId={focusedComponentId}
              setHoveredComponentId={setHoveredComponentId}
              setFocusedComponentId={setFocusedComponentId}
              showVertical={vertical}
              isRoot={false}
              isCollapsedReference={false}
              onComponentClick={onComponentClick}
              onComponentDoubleClick={onComponentDoubleClick}
              onReferenceClick={onReferenceClick}
              urlFieldValuesByComponentId={urlFieldValuesByComponentId}
              urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
              referenceIds={referenceIds}
              siblingCount={children.length - 1}
              contextMenuDataAttribute={contextMenuDataAttribute}
              isNewConditionalFormatting={isNewConditionalFormatting}
              DependencyMap={DependencyMap}
            />
          );
        })}
      </>
    );
  },
  areViewNodeChildrenEffectivelyEqual
);
export { DependencyMapViewNodeChildren };
